import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { MAINTENANCE_EVENT, MAINTENANCE_PATH, t } from '@bms2/shared';
import { apiFetch } from '../pages/records/api';
import { syncSessionMarker } from '../session';
import styles from './MaintenancePage.module.css';

/** How often the maintenance page asks whether the application is back (decision 4062). */
export const MAINTENANCE_POLL_MS = 5000;

/** What `GET /health/maintenance` answers. */
export interface MaintenanceAnswer {
  maintenance: boolean;
  since: string | null;
  message: string | null;
}

/**
 * 🔒 The full-screen maintenance page (piece 21; decision 4062): drawn for every
 * person while a recovery holds the API, over every route, the Login Screen's
 * included. 🔨 It draws no time: the session that would carry the person's
 * zone has been revoked, and the machine's own clock is not their setting.
 */
export function MaintenancePage() {
  return (
    <main className={styles.page} data-maintenance-page="true" role="alert" aria-live="assertive">
      <div className={styles.panel}>
        <h1 className={styles.title}>{t('shell.maintenance.title')}</h1>
        <p className={styles.body}>{t('shell.maintenance.body')}</p>
        <p className={styles.note}>{t('shell.maintenance.reloads')}</p>
        <p className={styles.note}>{t('shell.maintenance.byHand')}</p>
      </div>
    </main>
  );
}

/**
 * 🔒 The gate that swaps the application for the maintenance page on the first
 * 503 carrying the maintenance answer (decision 4062). It then asks the status
 * every five seconds; a refused or missing answer, as while the process
 * restarts, keeps the page drawn, and an answer saying maintenance is off
 * reloads. 🔨 The reload keeps the session token only where the server still
 * honors it (T2402): a Recover that failed before its switch revoked nothing
 * and signs nobody out, while one that switched revoked every session, so the
 * server's own session read says none is held and the token goes before the
 * reload lands on the Login Screen.
 */
export async function backFromMaintenance(apiBaseUrl = ''): Promise<void> {
  await syncSessionMarker(apiBaseUrl, (url) => apiFetch(url, {}, { skipAuthRedirect: true })).catch(/* silent: a session read that fails leaves the token as it stood, and the reload asks again. */ () => null);
  window.location.reload();
}

export function MaintenanceGate({ apiBaseUrl = '', children, onBack: given }: { apiBaseUrl?: string; children: ReactNode; onBack?: () => void }) {
  const [held, setHeld] = useState(false);
  const onBack = useMemo(() => given ?? (() => void backFromMaintenance(apiBaseUrl)), [given, apiBaseUrl]);

  useEffect(() => {
    const onMaintenance = () => setHeld(true);
    window.addEventListener(MAINTENANCE_EVENT, onMaintenance);
    return () => window.removeEventListener(MAINTENANCE_EVENT, onMaintenance);
  }, []);

  useEffect(() => {
    if (!held) return undefined;
    let stopped = false;
    const ask = async () => {
      // 🔨 No cookie rides the read: the session it would carry was revoked across the switch.
      const response = await fetch(`${apiBaseUrl}${MAINTENANCE_PATH}`, { credentials: 'omit' }).catch(/* silent: the API is restarting, and the page stays drawn until it answers. */ () => null);
      const answer = response?.ok ? ((await response.json().catch(/* silent: a half-started API answers no JSON, and the page asks again. */ () => null)) as MaintenanceAnswer | null) : null;
      if (stopped || !answer) return;
      if (answer.maintenance === false) onBack();
    };
    void ask();
    const timer = setInterval(() => void ask(), MAINTENANCE_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [held, apiBaseUrl, onBack]);

  return held ? <MaintenancePage /> : <>{children}</>;
}
